import { observer } from 'mobx-react-lite';
import { toast } from 'sonner';

import { Text } from '@/components/ui';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Switch } from '@/components/ui/Switch';
import { subscriptionStore } from '@/features/subscription';

import {
  type Skill,
  type TeamSkill,
  useSetTeamSkillStateMutation,
  useSkillsQuery,
} from './plugins-query';
import { useTeamSkillActions } from './use-team-skill-actions';

const SwitchRow = ({
  title,
  subtitle,
  checked,
  disabled,
  onCheckedChange,
}: {
  title: string;
  subtitle: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) => (
  <XStack className="items-start justify-between gap-3">
    <YStack className="min-w-0 gap-0.5">
      <Text className="text-sm font-medium">{title}</Text>
      <Text className="text-xs leading-5 text-foreground-muted">
        {subtitle}
      </Text>
    </YStack>
    <Switch
      aria-label={title}
      checked={checked}
      disabled={disabled}
      onCheckedChange={onCheckedChange}
    />
  </XStack>
);

export const TeamSkillRowControls = ({ team }: { team: TeamSkill }) => {
  const setState = useSetTeamSkillStateMutation();
  return (
    <XStack className="shrink-0 items-center gap-2 pr-4">
      {team.provisioned && <Badge variant="secondary">On for everyone</Badge>}
      <Switch
        aria-label="Enabled"
        checked={team.enabled}
        disabled={setState.isPending}
        onCheckedChange={(enabled) =>
          setState.mutate(
            { id: team.id, enabled },
            { onError: () => toast.error('Could not update this skill') }
          )
        }
      />
    </XStack>
  );
};

const OwnSkillTeamActions = observer(({ skill }: { skill: Skill }) => {
  const { teamSkills } = useSkillsQuery();
  const { shareSkill, removeFromTeam, setProvisioned, isPending } =
    useTeamSkillActions();
  const share = skill.teamShare;
  const team = teamSkills.find(
    (entry) => entry.teamSkill?.id === share?.id
  )?.teamSkill;
  const subtitle = !share
    ? 'Teammates can enable it, but not edit it'
    : share.is_stale
      ? 'Out of date since you edited it'
      : 'Shared with your team';
  return (
    <>
      <SwitchRow
        title="Share with team"
        subtitle={subtitle}
        checked={Boolean(share)}
        disabled={isPending || skill.isBroken}
        onCheckedChange={(checked) =>
          checked ? shareSkill(skill) : removeFromTeam(share!.id)
        }
      />
      {share?.is_stale && (
        <Button
          variant="secondary"
          size="sm"
          disabled={isPending}
          onClick={() => shareSkill(skill)}
        >
          Update team version
        </Button>
      )}
      {team && subscriptionStore.isTeamAdmin && (
        <SwitchRow
          title="On for everyone"
          subtitle="On by default for every member; each person can still turn it off"
          checked={team.provisioned}
          disabled={isPending}
          onCheckedChange={(provisioned) =>
            setProvisioned(team.id, provisioned)
          }
        />
      )}
    </>
  );
});

const TeamSkillDetailActions = observer(({ team }: { team: TeamSkill }) => {
  const { userSkills } = useSkillsQuery();
  const { setEnabled, setProvisioned, confirmRemove, isPending } =
    useTeamSkillActions();
  const isSharer = userSkills.some((own) => own.teamShare?.id === team.id);
  return (
    <>
      <YStack className="gap-3">
        <Text className="text-xs text-foreground-subtle">
          Shared by {team.shared_by ?? 'a former teammate'}
        </Text>
        <SwitchRow
          title="Enabled"
          subtitle="Littlebird can use this skill in your chats"
          checked={team.enabled}
          disabled={isPending}
          onCheckedChange={(enabled) => setEnabled(team.id, enabled)}
        />
        {subscriptionStore.isTeamAdmin && (
          <SwitchRow
            title="On for everyone"
            subtitle="On by default for every member; each person can still turn it off"
            checked={team.provisioned}
            disabled={isPending}
            onCheckedChange={(provisioned) =>
              setProvisioned(team.id, provisioned)
            }
          />
        )}
      </YStack>
      <YStack className="gap-2">
        {(isSharer || subscriptionStore.isTeamAdmin) && (
          <Button
            variant="destructive-ghost"
            size="sm"
            disabled={isPending}
            onClick={() => void confirmRemove(team.id)}
          >
            Remove from team
          </Button>
        )}
      </YStack>
    </>
  );
});

export const SkillTeamActions = observer(({ skill }: { skill: Skill }) => {
  if (!subscriptionStore.isTeamMember || skill.isSystem) return null;
  return (
    <YStack className="gap-4 border-t border-border pt-3">
      {skill.teamSkill ? (
        <TeamSkillDetailActions team={skill.teamSkill} />
      ) : (
        <OwnSkillTeamActions skill={skill} />
      )}
    </YStack>
  );
});
